// Path patterns for the sync engine (webdavsync-as-an-acequia-js-capability).
//
// Pure functions over pattern lists — no storage. The exclusion semantics are
// the app's, unchanged, so every saved configuration means what it meant:
//   *      any run of characters, INCLUDING slashes (the app's rule)
//   ?      one character
//   name/  a directory: matches the path, the filename, or any ancestor segment
// A pattern is tested against the whole path and against the bare filename,
// case-insensitively.
//
// Include patterns are new (the fixture, a backup of one folder and a
// selective publish are allowlists). A file is included when it matches any
// include pattern; a directory is traversed when an include pattern could
// still match beneath it.

/** The exclusions every configuration starts with (sync-configuration-manager's list). */
export const DEFAULT_EXCLUSIONS = Object.freeze([
    '*.local.js',
    '*.local.json',
    '.DS_Store',
    'Thumbs.db',
    '.git/',
    'node_modules/',
    '*.tmp',
    '*.log',
    '.env.local',
    'config.local.*',
])

const cache = new Map()

export function globToRegex(pattern) {
    let re = cache.get(pattern)
    if (re) return re
    let source = pattern
        .replace(/[.+^${}()|[\]\\]/g, '\\$&')
        .replace(/\*/g, '.*')
        .replace(/\?/g, '.')
    source = pattern.endsWith('/') ? `^${source}.*` : `^${source}$`
    re = new RegExp(source, 'i')
    cache.set(pattern, re)
    return re
}

function normalize(path) {
    return (path || '').replace(/^\/+/, '')
}

/** Does `path` match any pattern, by the exclusion rules above? */
export function matchesAny(path, patterns) {
    if (!path || typeof path !== 'string' || !patterns || patterns.length === 0) return false
    const normalized = normalize(path)
    const parts = normalized.split('/')
    const fileName = parts[parts.length - 1]
    for (const raw of patterns) {
        if (!raw || typeof raw !== 'string') continue
        const pattern = raw.trim()
        if (pattern === '') continue
        const re = globToRegex(pattern)
        if (re.test(normalized) || re.test(fileName)) return true
        if (pattern.endsWith('/')) {
            for (let i = 0; i < parts.length - 1; i++) {
                if (re.test(parts[i] + '/')) return true
            }
        }
    }
    return false
}

export const shouldExclude = matchesAny

/** A file is included when there are no include patterns, or it matches one. */
export function isIncluded(path, includes) {
    if (!includes || includes.length === 0) return true
    return matchesAny(path, includes)
}

/**
 * May an included file live under this directory? True when the directory
 * itself matches an include pattern (so everything beneath is in), or when
 * the pattern's literal prefix (before its first wildcard) begins with the
 * directory path — the walk has to descend to find out.
 */
export function dirMayContainIncluded(dirPath, includes) {
    if (!includes || includes.length === 0) return true
    const dir = normalize(dirPath).replace(/\/+$/, '')
    if (dir === '') return true
    for (const raw of includes) {
        if (!raw || typeof raw !== 'string') continue
        const pattern = normalize(raw.trim())
        if (pattern === '') continue
        if (matchesAny(dir, [pattern]) || matchesAny(dir + '/', [pattern])) return true
        const literal = pattern.split(/[*?]/)[0]
        // Not anchored to a path (`*.md`, `catalog.json`): it can match at
        // any depth, so every directory may hold one.
        if (!literal.includes('/')) return true
        if (literal.startsWith(dir + '/') || literal === dir) return true
    }
    return false
}

/** Files (and directories) that survive the exclusion list. */
export function filterExcluded(items, excludes) {
    if (!excludes || excludes.length === 0) return items
    return items.filter((item) => !matchesAny(item.path, excludes))
}
